import { QUERY_PARAMS, type AnalyticsQuery } from "@salesboard/contracts";
import { pipelineFilterQuery } from "@/features/opportunities/pipelineFilters";
import { TILE_MANAGED_KEYS } from "./tileFilters";

/**
 * The URL vocabulary analytics shares with the pipeline, and the links back into it.
 *
 * Pure functions, deliberately: "which URL does this tile open" is the kind of logic that
 * silently rots — a wrong parameter name produces a page that loads perfectly and shows the
 * wrong set. These are unit-tested; the components that use them are not.
 */

/**
 * The filter namespace, shared with the board and the list.
 *
 * The same namespace is what makes filters *travel*: a reader who filtered the board to one
 * client keeps that client filtered when the strip's Detailed view opens, because there is one
 * set of `opportunities:*` parameters and every surface reads it.
 */
export const PIPELINE_NAMESPACE = "opportunities";

/** The two tabs of the strip's Detailed view. Nothing else is a valid value. */
export const ANALYTICS_DETAIL_TABS = ["phase", "manager"] as const;
export type AnalyticsDetailTab = (typeof ANALYTICS_DETAIL_TABS)[number];

/**
 * The Detailed view's own URL key — both *whether* it is expanded and *which* tab is active.
 *
 * One parameter for both, because they are one fact: an expanded panel always has an active
 * tab, and the parameter is **deleted when the panel is collapsed** so a clean strip is a
 * clean URL. Tabs are view state the URL owns (ui-patterns.md), which is what makes the
 * expanded view linkable — the property the removed screen used to provide.
 */
export const ANALYTICS_DETAIL_PARAM = "analytics:detail";

/** The active tab, or `null` when the panel is collapsed. An unknown value is collapsed. */
export function detailTabFromSearch(search: string): AnalyticsDetailTab | null {
  const raw = new URLSearchParams(search).get(ANALYTICS_DETAIL_PARAM);
  return ANALYTICS_DETAIL_TABS.find((tab) => tab === raw) ?? null;
}

/**
 * The analytics request: the shared pipeline filters and the year scope, and nothing else.
 *
 * The filters go through `pipelineFilterQuery` — the same builder the board's request uses —
 * so the strip above the board and the board itself cannot filter differently, and a filter
 * added to or removed from the contract is a one-line change in one file rather than a
 * silent omission here.
 */
export function analyticsParams(apiParams: Record<string, string>, year: number | undefined): AnalyticsQuery {
  return {
    ...(year !== undefined ? { year } : {}),
    ...pipelineFilterQuery(apiParams),
  };
}

/**
 * A link to the pipeline, carrying the filters already on screen plus the ones a metric
 * implies. This is what makes a number openable — the book's rule is that a figure a user
 * cannot open is a figure they cannot act on.
 *
 * Three details that are not incidental:
 *
 * - **The Detailed view's expansion survives.** It used to be stripped along with the dead
 *   `analytics:scope`, on the grounds that analytics' own state meant nothing on the board.
 *   It means something now: the strip *is* on the board, so the panel snapping shut as the
 *   filters change beneath it would undo the reader's own click. Filters change; the view
 *   the reader chose to be looking through does not.
 * - **The page is dropped.** Page 4 of the previous result set is meaningless against a
 *   narrower one, and usually past its end.
 * - **A parameter at its default is deleted, not set.** `view=board` is the default, so a
 *   distribution bar's link is a clean URL rather than one asserting the default.
 *
 * **A link that speaks the tile vocabulary owns all of it.** If `filters` names any
 * `TILE_MANAGED_KEYS` key, *every* key in that set is cleared first and only the link's own
 * are applied. Without that the link started from the current search and merely *set* its own
 * keys, so a previous tile's survived: clicking Stalled after Open opened stalled-and-open,
 * which is neither tile's number, and the highlight — which compares the tile vocabulary —
 * lit nothing. Filters outside the set (`owner`, `client`, `q`, `year`, `view`) are never
 * touched: the reader set those themselves and no tile speaks for them.
 *
 * The rule is keyed on what the link *declares*, which is what makes the two Detailed-view
 * bars coherent rather than arbitrary:
 *
 * - A **phase bar** declares `stage` — a tile-managed key — so it resets the set like a tile
 *   does. Clicking Validation after the Closing tile gives Validation across the year, not
 *   Validation-in-Q3: the bar says "this phase", and a leftover quarter would make it say
 *   something the reader never asked for.
 * - A **manager bar** declares `owner`, which no tile manages, so it resets nothing and only
 *   narrows by owner. That is the honest behaviour twice over: there is no other manager bar
 *   whose filter it could be composing with, and the figure printed on that bar was computed
 *   by the server *under* the active phase/quarter/stalled filters — clearing them would open
 *   a set whose total is not the number the reader pressed.
 */
export function pipelineHref(
  currentSearch: string,
  options: { view?: "board" | "list"; filters?: Record<string, string | undefined> } = {},
): string {
  const params = new URLSearchParams(currentSearch);

  params.delete(QUERY_PARAMS.page);

  const declared = Object.keys(options.filters ?? {});
  if (declared.some((key) => TILE_MANAGED_KEYS.includes(key))) {
    for (const key of TILE_MANAGED_KEYS) params.delete(`${PIPELINE_NAMESPACE}:${key}`);
  }

  for (const [key, value] of Object.entries(options.filters ?? {})) {
    const name = `${PIPELINE_NAMESPACE}:${key}`;
    if (value === undefined || value === "") params.delete(name);
    else params.set(name, value);
  }

  const view = `${PIPELINE_NAMESPACE}:view`;
  if (options.view === "list") params.set(view, "list");
  else if (options.view === "board") params.delete(view);

  const query = params.toString();
  return query ? `/?${query}` : "/";
}

/**
 * The same address with one filter **removed**, and nothing else touched.
 *
 * The toggle-off half of a tile press (2026-08-26). It deliberately does *not* go through
 * `pipelineHref`: naming a tile-managed key there clears the whole tile vocabulary first,
 * which is right when a tile is asserting its own combination and wrong when it is
 * withdrawing one. A reader who filtered the board to one manager and then pressed Stalled
 * twice asked for stalled to go, not for the manager to go with it.
 *
 * The page is dropped for the same reason it is dropped on the way in: page 4 of the narrower
 * set is usually past the end of the wider one.
 */
export function pipelineHrefWithout(currentSearch: string, keys: string[]): string {
  const params = new URLSearchParams(currentSearch);
  params.delete(QUERY_PARAMS.page);
  for (const key of keys) params.delete(`${PIPELINE_NAMESPACE}:${key}`);
  const query = params.toString();
  return query ? `/?${query}` : "/";
}
